import { useState } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import type { PowerCabinet } from '@/types/synoptique'
import { useLanguage } from "@/lib/i18n/language-provider"

interface CustomCabinetDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onCreateCabinet: (cabinet: PowerCabinet) => void
}

interface CustomCabinetForm {
  name: string
  type: string
  maxPower: string
  outlets: string
  voltage: string
  dimensionsWidth: string
  dimensionsHeight: string
}

const initialForm: CustomCabinetForm = {
  name: '',
  type: '',
  maxPower: '',
  outlets: '',
  voltage: '',
  dimensionsWidth: '',
  dimensionsHeight: ''
}

export function CustomCabinetDialog({
  open,
  onOpenChange,
  onCreateCabinet
}: CustomCabinetDialogProps) {
  const { t } = useLanguage()
  const [form, setForm] = useState<CustomCabinetForm>(initialForm)

  const handleCreate = () => {
    const maxPower = parseInt(form.maxPower) || 0
    const outlets = parseInt(form.outlets) || 0

    if (maxPower <= 0 || outlets <= 0) {
      return
    }

    const newCabinet: PowerCabinet = {
      id: `custom-cabinet-${Date.now()}`,
      name: form.name || `Custom ${maxPower}W`,
      brand: 'Custom',
      type: form.type || 'PDU',
      maxPower,
      outlets,
      voltage: form.voltage || '230V',
      dimensions: {
        width: parseInt(form.dimensionsWidth) || 100,
        height: parseInt(form.dimensionsHeight) || 200
      }
    }

    onCreateCabinet(newCabinet)
    setForm(initialForm)
    onOpenChange(false)
  }

  const handleClose = () => {
    setForm(initialForm)
    onOpenChange(false)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Créer une armoire électrique personnalisée</DialogTitle>
          <DialogDescription>
            Entrez les spécifications de votre armoire électrique. Seuls la puissance maximale et le nombre de prises sont obligatoires.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          {/* Nom */}
          <div className="space-y-2">
            <Label htmlFor="cabinetName" className="text-sm font-semibold">Nom de l'armoire</Label>
            <Input
              id="cabinetName"
              type="text"
              placeholder="ex: PDU 10kW"
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              Si vide, le nom sera généré automatiquement (ex: Custom 10000W)
            </p>
          </div>

          {/* Type */}
          <div className="space-y-2">
            <Label htmlFor="cabinetType" className="text-sm font-semibold">Type</Label>
            <Input
              id="cabinetType"
              type="text"
              placeholder="ex: PDU, Distribution Panel, Power Supply"
              value={form.type}
              onChange={(e) => setForm(prev => ({ ...prev, type: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              Par défaut: PDU
            </p>
          </div>

          {/* Puissance max */}
          <div className="space-y-2">
            <Label htmlFor="maxPower" className="text-sm font-semibold">Puissance maximale (W) *</Label>
            <Input
              id="maxPower"
              type="number"
              placeholder="ex: 10000"
              value={form.maxPower}
              onChange={(e) => setForm(prev => ({ ...prev, maxPower: e.target.value }))}
              required
            />
          </div>

          {/* Nombre de prises */}
          <div className="space-y-2">
            <Label htmlFor="outlets" className="text-sm font-semibold">{t("synoptic.outletsCount")} *</Label>
            <Input
              id="outlets"
              type="number"
              placeholder="ex: 8"
              value={form.outlets}
              onChange={(e) => setForm(prev => ({ ...prev, outlets: e.target.value }))}
              required
            />
          </div>

          {/* Voltage */}
          <div className="space-y-2">
            <Label htmlFor="voltage" className="text-sm font-semibold">Voltage</Label>
            <Input
              id="voltage"
              type="text"
              placeholder="ex: 230V, 400V (optionnel)"
              value={form.voltage}
              onChange={(e) => setForm(prev => ({ ...prev, voltage: e.target.value }))}
            />
            <p className="text-xs text-muted-foreground">
              Par défaut: 230V
            </p>
          </div>

          {/* Dimensions */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="cabinetDimensionsWidth" className="text-sm font-semibold">Largeur (mm)</Label>
              <Input
                id="cabinetDimensionsWidth"
                type="number"
                placeholder="ex: 100 (optionnel)"
                value={form.dimensionsWidth}
                onChange={(e) => setForm(prev => ({ ...prev, dimensionsWidth: e.target.value }))}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="cabinetDimensionsHeight" className="text-sm font-semibold">Hauteur (mm)</Label>
              <Input
                id="cabinetDimensionsHeight"
                type="number"
                placeholder="ex: 200 (optionnel)"
                value={form.dimensionsHeight}
                onChange={(e) => setForm(prev => ({ ...prev, dimensionsHeight: e.target.value }))}
              />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={handleClose}>
            Annuler
          </Button>
          <Button onClick={handleCreate}>
            Créer l'armoire
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
